import * as React from 'react';
import addViewportFitCover from '../utils/addViewportFitCover';
import getModuleSwitcherItems, { ModuleSwitcherIconRefs } from '../utils/getModuleSwitcherItems';
import Module from '../schema/Module';
import ModuleSwitcherItem from '../schema/ModuleSwitcherItem';
import onModuleSwitcherItemClick from '../utils/onModuleSwitcherItemClick';
import strings from 'owa-strings/owa-mini-common-strings';
import { CalendarIconDefault } from 'mini-calendar-icon/lib/components/CalendarIconDefault';
import { isIOS } from 'mini-device-utils/lib/utils/isIOS';
import { isReactCalendarEnabled } from '../utils/reactOptinSettingsUtils';
import { observer } from 'mobx-react';

const styles = require('./ModuleSwitcher.scss');
const classNames = require('classnames/bind').bind(styles);

export interface ModuleSwitcherProps {
    currentModule: Module;
    onActiveModuleClickAction?: () => void;
    renderCalendarIcon?: (ref: React.RefObject<any>) => JSX.Element;
    iconRefs?: ModuleSwitcherIconRefs;
}

@observer
export default class ModuleSwitcher extends React.Component<
    ModuleSwitcherProps,
    { willOverflow: boolean }
> {
    constructor(props: ModuleSwitcherProps) {
        super(props);
        this.state = {
            willOverflow: false,
        };
    }
    componentDidMount() {
        addViewportFitCover();
        let element = document.getElementById('moduleSwitchFocusElement');
        let boolOverflow = false;
        if (element != null) {
            boolOverflow =
                element.scrollHeight > element.clientHeight ||
                element.scrollWidth > element.clientWidth;
        }
        this.setState({ willOverflow: boolOverflow });
    }

    render() {
        const { iconRefs } = this.props;

        return (
            <nav
                id="moduleSwitchFocusElement"
                className={classNames(styles.moduleSwitcher, {
                    [styles.moduleSwitcherOverflow]: this.state.willOverflow,
                    [styles.iosSafeZone]: isIOS(),
                })}
                role="heading"
                aria-level={1}
                aria-label={strings.moduleSwitcherFooterAriaLabel}>
                {getModuleSwitcherItems(iconRefs).map(this.renderModuleSwitcherItem)}
            </nav>
        );
    }

    renderCalendarItem = (isSelected, iconClassName, iconRef) => {
        const { renderCalendarIcon = ref => <CalendarIconDefault ref={ref} /> } = this.props;
        /**
         * SDF users can still see mini react calendar even though they are not flighted for it through backdoor entry
         * Added the isSelected check to handle that case also.
         */
        return isReactCalendarEnabled() || isSelected ? (
            renderCalendarIcon(iconRef)
        ) : (
            <i className={iconClassName} ref={iconRef} />
        );
    };

    private renderModuleSwitcherItem = (item: ModuleSwitcherItem) => {
        const { currentModule, onActiveModuleClickAction } = this.props;
        const { moduleType, icon, ariaLabel, ariaLabelWhenSelected, iconRef, title } = item;
        const isSelected = moduleType === currentModule;
        const buttonClasses = classNames('moduleSwitcher-button', {
            'moduleSwitcher-button--selected': isSelected,
        });
        const iconClassName = classNames('ms-Icon', icon);
        return (
            <span
                key={`${icon}-${moduleType}`}
                className={classNames(buttonClasses, item.className)}
                onClick={() =>
                    onModuleSwitcherItemClick(currentModule, moduleType, onActiveModuleClickAction)
                }
                onKeyPress={e => {
                    if (e.key !== 'Enter') {
                        return;
                    }
                    onModuleSwitcherItemClick(currentModule, moduleType, onActiveModuleClickAction);
                }}
                role="button"
                aria-label={isSelected ? ariaLabelWhenSelected : ariaLabel}
                title={title || ariaLabel}
                tabIndex={0}>
                {moduleType === Module.Calendar ? (
                    this.renderCalendarItem(isSelected, iconClassName, iconRef)
                ) : (
                    <i className={iconClassName} ref={iconRef} />
                )}
            </span>
        );
    };
}
